// apps/web/src/lib/api.ts — GraphQL client wrapper with auth + XSRF + i18n headers.
//
// Per Phase 2 plan 02-02: the access token is held in memory by
// lib/auth.tsx (D-02) and read here via getAccessToken(). The XSRF
// cookie is read from document.cookie (D-01's double-submit pattern).
// Per D-15, every request also carries `X-Lang` so the server can
// choose the correct messageKey if it ever wants to.
//
// Phase 9 plan 09-01 (D-09-01..03): every outgoing request now also
// carries `extensions.persistedQuery = { sha256Hash, version: 1 }`
// for the operation being sent. The hash is looked up from the
// build-time `PERSISTED_OPS_CLIENT_MAP` (apps/web/src/lib/persisted-
// ops.client.ts — generated by `pnpm build:persisted-ops`). The
// server-side gate (apps/api/src/lib/persisted-operations.ts) verifies
// the hash against the matching seed and rejects unknown operations
// with 403 + PERSISTED_OPERATION_REQUIRED in production.
//
// The `silentRefresh` interceptor (D-03): on a 401 / UNAUTHENTICATED
// response, the wrapper awaits `silentRefresh()` (one in-flight at a
// time) and retries the original request ONCE. If the retry also
// fails with UNAUTHENTICATED, the error propagates to RequireAuth
// which redirects to /login.

import {
  ClientError,
  GraphQLClient,
  type RequestDocument,
  type Variables,
} from "graphql-request";
import i18n from "i18next";
import { PERSISTED_OPS_CLIENT_MAP } from "./persisted-ops.client";

// Module-scope in-memory access token mirror. The AuthProvider writes
// here via setAccessToken(); getters read here directly so graphql-request
// headers re-fetch on every call (the access token rotates).
let accessToken: string | null = null;

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function readXsrfCookie(): string | null {
  if (typeof document === "undefined") return null;
  const cookies = document.cookie.split(";");
  for (const entry of cookies) {
    const [rawK, ...rest] = entry.trim().split("=");
    if (rawK === "XSRF-TOKEN") {
      return decodeURIComponent(rest.join("="));
    }
  }
  return null;
}

// The REFRESH_MUTATION + LOGOUT_MUTATION documents live here so the
// auth module can call them through the standard gqlRequest path
// (which gives it the 401-retry interceptor for free). Defined as
// constants rather than `gql` template tags because the auth provider
// does not depend on the graphql-tag runtime.
const REFRESH_DOC = /* GraphQL */ `
  mutation SilentRefresh {
    refresh {
      accessToken
      expiresIn
    }
  }
`;

const LOGOUT_DOC = /* GraphQL */ `
  mutation SilentLogout {
    logout
  }
`;

// Dedupe in-flight refresh calls (D-03): if 3 requests fire 401 at
// the same instant, only ONE refresh runs; the other 2 await its
// result. After the first refresh resolves, the in-flight promise
// is reset so the next 401 cycle can kick off a new refresh.
let refreshPromise: Promise<void> | null = null;

export interface SilentRefreshOptions {
  onReuse?: () => void;
  onMissing?: () => void;
}

let refreshOptions: SilentRefreshOptions = {};

export function setSilentRefreshOptions(opts: SilentRefreshOptions): void {
  refreshOptions = opts;
}

async function performSilentRefresh(): Promise<void> {
  const client = getGqlClient();
  const result = await client.rawRequest<{
    refresh: { accessToken: string; expiresIn: number };
  }>(REFRESH_DOC, {});
  const data = result.data;
  if (!data?.refresh) {
    throw new Error("silentRefresh: no data");
  }
  accessToken = data.refresh.accessToken;
  // Schedule the next preemptive refresh via a CustomEvent the
  // AuthProvider listens for. We can't directly import auth.tsx here
  // without a circular import (auth.tsx imports from api.ts), so we
  // dispatch a window event the provider handles.
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("gastio:access-token", {
        detail: {
          accessToken: data.refresh.accessToken,
          expiresIn: data.refresh.expiresIn,
        },
      }),
    );
  }
}

export async function silentRefresh(): Promise<void> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      await performSilentRefresh();
    } catch (err) {
      // REFRESH_TOKEN_REUSED, CSRF_TOKEN_MISMATCH, or missing cookie →
      // clear local access token and notify the AuthProvider.
      if (err instanceof ClientError) {
        const code = err.response.errors?.[0]?.extensions?.code;
        if (code === "REFRESH_TOKEN_REUSED" || code === "CSRF_TOKEN_MISMATCH") {
          accessToken = null;
          refreshOptions.onReuse?.();
        } else {
          accessToken = null;
          refreshOptions.onMissing?.();
        }
      } else {
        accessToken = null;
        refreshOptions.onMissing?.();
      }
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

let cachedClient: GraphQLClient | null = null;

export function getGqlClient(): GraphQLClient {
  if (cachedClient) return cachedClient;
  cachedClient = new GraphQLClient("/graphql", {
    // Phase 7 plan 07-06 (B2 fix + D-07-15): credentials: "include"
    // is REQUIRED for the signup/verify/onboarding flow because the
    // onboarding_access cookie is set with Domain=.<env.BASE_DOMAIN>
    // so it auto-flows across subdomains (the verify page may run on
    // gastio.app while the onboarding wizard runs on <slug>.gastio.app).
    // Without credentials the Set-Cookie from verify would be dropped
    // by the browser.
    credentials: "include",
    headers: () => {
      // The self-service token is read on every request from the
      // in-memory store (T-04-32). When the token is unset (the
      // wizard / admin paths), the header is omitted entirely —
      // sending an empty `x-self-service-token` would invite the
      // server to evaluate it as a credential lookup and fall
      // through to `SELF_SERVICE_TOKEN_INVALID` (D-02 collapses
      // empty + invalid into the same code). The server's resolver
      // also reads the token from `input.token` when provided, so
      // an empty header is fine for the wizard mutations.
      const selfServiceToken = getSelfServiceTokenStore().token;
      // Phase 7 plan 07-06 (D-07-15): in dev path-based mode the
      // /t/<slug>/ prefix tells the API which tenant the request is
      // for. In prod the Fastify resolve-tenant plugin reads the
      // Host header directly so the slug header is omitted.
      const tenantSlugHeader = (() => {
        if (typeof window === "undefined") return null;
        if (!import.meta.env.DEV) return null;
        const m = window.location.pathname.match(
          /^\/t\/([a-z0-9-]{3,40})(\/|$)/,
        );
        return m && typeof m[1] === "string" ? m[1] : null;
      })();
      return {
        ...(getAccessToken()
          ? { Authorization: `Bearer ${getAccessToken()}` }
          : {}),
        ...(readXsrfCookie()
          ? { "X-XSRF-TOKEN": readXsrfCookie() as string }
          : {}),
        ...(selfServiceToken
          ? { "x-self-service-token": selfServiceToken }
          : {}),
        ...(tenantSlugHeader ? { "x-tenant-slug": tenantSlugHeader } : {}),
        "X-Lang": i18n.language.startsWith("en") ? "en" : "de",
      };
    },
    // Don't throw on non-2xx; we want to inspect extensions.code ourselves.
    errorPolicy: "all",
    // Phase 9 plan 09-01 (D-09-01..03): inject the persisted-query
    // hash into every outgoing POST body. graphql-request composes
    // the body as `{ query, variables, operationName }`; we add
    // `extensions.persistedQuery = { sha256Hash, version: 1 }`
    // here. The server-side gate (apps/api/src/lib/persisted-
    // operations.ts) verifies the hash against the build-time seed
    // and rejects unknown operations with 403 in production. The
    // map is generated by `pnpm build:persisted-ops`; anonymous
    // operations or operations missing from the map simply omit
    // the extension (dev/test only — production is strict).
    requestMiddleware: (request) => {
      const opName = request.operationName;
      const hash =
        typeof opName === "string"
          ? PERSISTED_OPS_CLIENT_MAP[opName]
          : undefined;
      if (!hash) return request;
      const rawBody = request.body;
      if (typeof rawBody !== "string") return request;
      let payload: Record<string, unknown>;
      try {
        payload = JSON.parse(rawBody) as Record<string, unknown>;
      } catch {
        return request;
      }
      const existing =
        typeof payload.extensions === "object" && payload.extensions !== null
          ? (payload.extensions as Record<string, unknown>)
          : {};
      payload.extensions = {
        ...existing,
        persistedQuery: { sha256Hash: hash, version: 1 },
      };
      return { ...request, body: JSON.stringify(payload) };
    },
  });
  return cachedClient;
}

// Operations that should NOT trigger a silent refresh retry — refresh
// itself, login, and logout are the ones that cause the 401 in the
// first place, so retrying them is pointless and would loop forever.
const NO_RETRY_OPS = new Set<string>([
  "GetRestaurant",
  "SilentRefresh",
  "LoginAuth",
  "LoginPage",
  "SilentLogout",
]);

function getOperationName(doc: RequestDocument): string | null {
  if (typeof doc === "string") {
    const m = doc.match(/(?:mutation|query)\s+(\w+)/);
    return m ? (m[1] ?? null) : null;
  }
  // DocumentNode: walk the definitions.
  const defs = (
    doc as unknown as {
      definitions?: Array<{ name?: { value?: string }; operation?: string }>;
    }
  ).definitions;
  if (!defs || defs.length === 0) return null;
  const first = defs[0];
  return first?.name?.value ?? null;
}

export async function gqlRequest<T>(
  doc: RequestDocument,
  vars?: Variables,
): Promise<T> {
  const opName = getOperationName(doc);
  const isNoRetry = opName ? NO_RETRY_OPS.has(opName) : false;

  try {
    const data = await getGqlClient().request<T>(doc, vars);
    return data;
  } catch (err) {
    if (
      err instanceof ClientError &&
      !isNoRetry &&
      err.response.errors?.some((e) => e.extensions?.code === "UNAUTHENTICATED")
    ) {
      // Try silent refresh + retry once.
      await silentRefresh();
      if (getAccessToken()) {
        const data = await getGqlClient().request<T>(doc, vars);
        return data;
      }
    }
    throw err;
  }
}

// Re-export the LOGOUT document so auth.tsx can call the mutation
// directly (it needs to fire BEFORE clearing the in-memory token so
// the server-side row revocation runs).
export { LOGOUT_DOC };

// ============================================================
// Phase 4 plan 04-03: wizard helpers (A4..A7).
//
// The wizard uses the same `getGqlClient` singleton as the rest
// of the web bundle; the typed wrappers below centralize the
// operation names + return shapes so the A4..A7 components
// don't repeat the gql template literals.
// ============================================================

import {
  CREATE_RESERVATION,
  GET_AVAILABLE_SLOTS,
  GET_RESTAURANT,
} from "../wizard/queries";
import type { CreateReservationInput } from "../wizard/schema";
// Phase 4 plan 04-04: self-service surface. The three wrappers
// read the raw token from the in-memory store (T-04-32: never
// localStorage) and dispatch through the same `gqlRequest` path
// the wizard uses. The `x-self-service-token` header is injected
// by the header-builder below.
import {
  CANCEL_RESERVATION_BY_TOKEN,
  GET_RESERVATION_BY_TOKEN,
  MODIFY_RESERVATION_BY_TOKEN,
} from "../self-service/queries";
import { getSelfServiceTokenStore } from "../self-service/state";

export interface RestaurantProfile {
  id: string;
  slug: string;
  timezone: string;
  confirmationMode: "auto" | "manual";
  areasEnabled: boolean;
  consentText: { de: string; en: string | null } | null;
}

export interface AvailableSlot {
  id: string;
  startTime: string;
  startsAt: string;
  endsAt: string;
  durationMinutes: number;
  remainingSeats: number;
  weekday: string;
  // Optional status flag the test suite (and a future server
  // expansion) uses to disable rows that the server's `slots`
  // query doesn't yet surface. The default is "AVAILABLE";
  // `remainingSeats === 0` flips to "FULL".
  status?: "AVAILABLE" | "BLOCKED" | "FULL" | "OUTSIDE_BOOKING_HOURS";
}

export interface CreateReservationResult {
  reservationNumber: string;
  selfServiceToken: string;
  selfServiceUrl: string;
  expiresAt: string;
}

export async function getRestaurant(): Promise<RestaurantProfile> {
  // GET_RESTAURANT now hits `Query.publicBookingContext` (not the
  // admin-gated `Query.restaurant`) — the wizard is unauthenticated,
  // so unwrapping the matching response key. See the Phase-4 follow-up
  // note in apps/web/src/wizard/queries.ts for context.
  const data = await gqlRequest<{ publicBookingContext: RestaurantProfile }>(
    GET_RESTAURANT,
  );
  return data.publicBookingContext;
}

export async function getAvailableSlots(args: {
  date: string;
  partySize: number;
}): Promise<AvailableSlot[]> {
  const data = await gqlRequest<{ slots: AvailableSlot[] }>(
    GET_AVAILABLE_SLOTS,
    { date: args.date, partySize: args.partySize },
  );
  return data.slots;
}

export async function createReservation(args: {
  input: CreateReservationInput;
  captchaToken: string;
}): Promise<CreateReservationResult> {
  const data = await gqlRequest<{
    createReservation: CreateReservationResult;
  }>(CREATE_RESERVATION, {
    input: args.input,
    captchaToken: args.captchaToken,
  });
  return data.createReservation;
}

// ============================================================
// Phase 4 plan 04-04: self-service wrappers (SELF-01..03).
//
// The token argument is required on the surface even though the
// `x-self-service-token` header carries the credential: the server
// resolver reads the token from `input.token` (modify/cancel) or
// `args.token` (reservationByToken) and falls back to the header
// only when the input is empty (04-02 §resolveTokenArg). The web
// client always passes the token in BOTH places for belt-and-
// braces (the input wins on conflict).
// ============================================================

export interface SelfServiceReservationGql {
  id: string;
  reservationNumber: string;
  status: string;
  partySize: number;
  serviceDate: string;
  slotDefinitionId: string;
  startsAt: string;
  durationMinutes: number;
  areaId: string | null;
  confirmationMode: "AUTO" | "MANUAL";
  createdAt: string;
  consentAt: string;
  isCancellable: boolean;
}

export interface ModifyReservationInput {
  token: string;
  serviceDate: string;
  slotDefinitionId: string;
  startsAt: string;
  durationMinutes: number;
  partySize: number;
  areaId: string | null;
  name: string;
}

export interface CancelReservationInput {
  token: string;
  reason: string | null;
}

export async function getReservationByToken(
  token: string,
): Promise<SelfServiceReservationGql> {
  const data = await gqlRequest<{
    reservationByToken: SelfServiceReservationGql;
  }>(GET_RESERVATION_BY_TOKEN, { token });
  return data.reservationByToken;
}

export async function modifyReservationByToken(
  input: ModifyReservationInput,
): Promise<SelfServiceReservationGql> {
  const data = await gqlRequest<{
    modifyReservationByToken: SelfServiceReservationGql;
  }>(MODIFY_RESERVATION_BY_TOKEN, { input });
  return data.modifyReservationByToken;
}

export async function cancelReservationByToken(
  input: CancelReservationInput,
): Promise<SelfServiceReservationGql> {
  const data = await gqlRequest<{
    cancelReservationByToken: SelfServiceReservationGql;
  }>(CANCEL_RESERVATION_BY_TOKEN, { input });
  return data.cancelReservationByToken;
}
